import { Component, computed, effect, input, output, signal } from '@angular/core'
import { ScheduleRuleCardComponent } from '../../entities/schedule-rule-card/schedule-rule-card.component'
import { ScheduleRule } from '../../models/schedule-rule.model'

@Component({
  selector: 'app-schedule-rules-infinite-scroll',
  standalone: true,
  imports: [ScheduleRuleCardComponent],
  templateUrl: './schedule-rules-infinite-scroll.component.html',
  styleUrl: './schedule-rules-infinite-scroll.component.scss'
})
export class ScheduleRulesInfiniteScrollComponent {
  // Inputs
  allRules = input.required<ScheduleRule[]>()
  filteredRules = input.required<ScheduleRule[]>()
  shopNameMap = input.required<Map<string, string>>()
  userNameMap = input.required<Map<string, string>>()

  // Outputs
  toggleActive = output<{ id: string; active: boolean }>()
  updateWeekdays = output<{ id: string; weekdays: number[] }>()
  requestUpdateFromAdmin = output<string>()
  updateSchedulingRequirements = output<{ shopId: string; requirements: string }>()

  // Infinite scroll state
  private readonly ITEMS_PER_PAGE = 5
  displayedItemsCount = signal<number>(this.ITEMS_PER_PAGE * 2)

  // Track all rules that have been rendered at least once
  private renderedRuleIds = new Set<string>()

  // Computed signal for visible rules (includes all previously rendered)
  visibleRules = computed(() => {
    const filtered = this.filteredRules()
    const displayCount = this.displayedItemsCount()

    // Add new rules to rendered set up to displayCount
    filtered.slice(0, displayCount).forEach(rule => {
      this.renderedRuleIds.add(rule.id)
    })

    // Return all rendered rules, maintaining the sort order from filteredRules
    const renderedIds = this.renderedRuleIds

    // First, get all currently filtered rules that have been rendered (in sorted order)
    const currentFilteredRendered = filtered.filter(rule => renderedIds.has(rule.id))

    // Then, get any previously rendered rules that are no longer in the filtered list
    const previouslyRendered = this.allRules().filter(
      rule => renderedIds.has(rule.id) && !filtered.some(f => f.id === rule.id)
    )

    // Combine them: current filtered (sorted) + previously rendered (will be hidden)
    return [...currentFilteredRendered, ...previouslyRendered]
  })

  // Computed signal to determine which rules should be visible (not hidden)
  visibleRuleIds = computed(() => {
    return new Set(this.filteredRules().map(rule => rule.id))
  })

  hasMoreItems = computed(() => {
    return this.displayedItemsCount() < this.filteredRules().length
  })

  constructor() {
    // Reset displayed items when filtered rules change
    effect(() => {
      this.filteredRules() // Track changes
      this.displayedItemsCount.set(this.ITEMS_PER_PAGE * 2)
    })
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement
    const threshold = 200 // pixels from bottom to trigger load
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold

    if (atBottom && this.hasMoreItems()) {
      this.loadMoreItems()
    }
  }

  private loadMoreItems() {
    const currentCount = this.displayedItemsCount()
    const newCount = Math.min(currentCount + this.ITEMS_PER_PAGE, this.filteredRules().length)
    this.displayedItemsCount.set(newCount)
  }

  isRuleVisible(ruleId: string): boolean {
    return this.visibleRuleIds().has(ruleId)
  }

  onToggleActive(id: string, active: boolean) {
    this.toggleActive.emit({ id, active })
  }

  onUpdateWeekdays(id: string, weekdays: number[]) {
    this.updateWeekdays.emit({ id, weekdays })
  }

  onRequestUpdateFromAdmin(shopId: string) {
    this.requestUpdateFromAdmin.emit(shopId)
  }

  onUpdateSchedulingRequirements(shopId: string, requirements: string) {
    this.updateSchedulingRequirements.emit({ shopId, requirements })
  }
}
